import { create } from 'zustand';
import { FEATURES } from '@config/features';

/**
 * Temporary developer-only observability layer (Step 3 of the connectivity
 * investigation: "measure, observe, record" — no thresholds, no judgments).
 *
 * Every recorder below is a no-op unless FEATURES.NETWORK_DIAGNOSTICS is on
 * (the same flag that mounts the panel in App.jsx), so a real patient
 * session pays one boolean check per instrumented call site and nothing
 * else — no extra network traffic, no persisted state, nothing that can
 * change what the app does. `record()` never throws: telemetry must never be
 * able to break the feature it observes (same rule the realtime store's
 * `captureOnce` already follows for Sentry).
 *
 * Records match the data model proposed in Step 2:
 *   { timestamp, sessionId, component, metric, value, unit, endpoint,
 *     networkType, downlink, rtt, userVisibleImpact, clientContext }
 */
const MAX_RECORDS = 800;

const isEnabled = () => FEATURES.NETWORK_DIAGNOSTICS;

const nowIso = () => new Date().toISOString();

const readNetworkInfo = () => {
  try {
    const connection =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) {
      return { networkType: null, downlink: null, rtt: null };
    }
    return {
      networkType: connection.effectiveType ?? null,
      downlink: typeof connection.downlink === 'number' ? connection.downlink : null,
      rtt: typeof connection.rtt === 'number' ? connection.rtt : null,
    };
  } catch (_error) {
    // navigator.connection is a browser heuristic, not a measurement — Step 2
    // flagged it as absent on Safari/iOS. Absence is just null, not an error.
    return { networkType: null, downlink: null, rtt: null };
  }
};

const resolveClientContext = () => {
  try {
    return `${navigator.platform || 'unknown'} | ${navigator.userAgent || 'unknown'}`;
  } catch (_error) {
    return 'unknown';
  }
};

export const useNetworkDiagnostics = create((set, get) => ({
  sessionId: null,
  records: [],

  setSessionId: sessionId => set({ sessionId }),

  /**
   * `component`: 'websocket' | 'rest' | 'media' | 'webrtc'.
   * `extra.endpoint`, `extra.userVisibleImpact` are optional, per the Step 2
   * data model.
   */
  record: (component, metric, value, unit, extra = {}) => {
    if (!isEnabled()) {
      return;
    }

    try {
      const { networkType, downlink, rtt } = readNetworkInfo();
      const entry = {
        timestamp: nowIso(),
        sessionId: get().sessionId,
        component,
        metric,
        value,
        unit,
        endpoint: extra.endpoint ?? null,
        networkType,
        downlink,
        rtt,
        userVisibleImpact: extra.userVisibleImpact ?? null,
        clientContext: resolveClientContext(),
      };

      set(state => ({
        records:
          state.records.length >= MAX_RECORDS
            ? [...state.records.slice(1), entry]
            : [...state.records, entry],
      }));
    } catch (_error) {
      // Never let instrumentation break the feature it's observing.
    }
  },

  clear: () => set({ records: [] }),
}));

// --- Pure read-only selectors over the record ring, used only by the debug
// panel. Kept outside the store so they're plain functions, not reactive
// state — the panel re-derives a snapshot on its own poll tick instead of
// re-rendering on every single record push (which would be one push per
// WebSocket frame, ~30/s).

const withinWindow = (records, windowMs, now = Date.now()) =>
  records.filter(entry => now - Date.parse(entry.timestamp) <= windowMs);

const byMetric = (records, component, metric) =>
  records.filter(entry => entry.component === component && entry.metric === metric);

const percentile = (values, p) => {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[index];
};

const latestValue = entries => (entries.length ? entries[entries.length - 1].value : null);

const sumValues = entries => entries.reduce((total, entry) => total + (entry.value || 0), 0);

export function selectWebSocketSnapshot(records) {
  const recent = withinWindow(records, 5000);
  const outboundBytes = byMetric(recent, 'websocket', 'outbound_message_bytes');
  const inboundBytes = byMetric(recent, 'websocket', 'inbound_message_bytes');
  const latencySamples = byMetric(withinWindow(records, 30000), 'websocket', 'message_latency').map(
    entry => entry.value
  );
  const reconnects = byMetric(records, 'websocket', 'reconnect_scheduled');
  const lastOpen = byMetric(records, 'websocket', 'connection_success');
  const lastClose = byMetric(records, 'websocket', 'connection_closed');
  const lastError = byMetric(records, 'websocket', 'connection_error');
  const setupTimes = byMetric(records, 'websocket', 'connection_setup_time');

  const lastOpenAt = lastOpen.length ? Date.parse(lastOpen[lastOpen.length - 1].timestamp) : -1;
  const lastCloseAt = lastClose.length ? Date.parse(lastClose[lastClose.length - 1].timestamp) : -1;
  const lastErrorAt = lastError.length ? Date.parse(lastError[lastError.length - 1].timestamp) : -1;

  let connectionState = 'unknown';
  if (lastOpenAt >= lastCloseAt && lastOpenAt >= lastErrorAt && lastOpenAt !== -1) {
    connectionState = 'connected';
  } else if (lastErrorAt >= lastOpenAt && lastErrorAt !== -1) {
    connectionState = 'error';
  } else if (lastCloseAt >= lastOpenAt && lastCloseAt !== -1) {
    connectionState = 'closed';
  }

  return {
    connectionState,
    setupMs: latestValue(setupTimes),
    messagesPerSec: Number((outboundBytes.length / 5).toFixed(1)),
    uploadKBs: Number((sumValues(outboundBytes) / 1024 / 5).toFixed(2)),
    downloadKBs: Number((sumValues(inboundBytes) / 1024 / 5).toFixed(2)),
    rttP50: percentile(latencySamples, 50),
    rttP95: percentile(latencySamples, 95),
    reconnectCount: reconnects.length,
  };
}

export function selectRestSnapshot(records) {
  const recent = withinWindow(records, 60000);
  const durations = byMetric(recent, 'rest', 'request_duration');
  const errors = byMetric(recent, 'rest', 'request_error');
  const ttfbs = byMetric(recent, 'rest', 'ttfb').map(entry => entry.value);
  const values = durations.map(entry => entry.value);

  return {
    requestCount: durations.length,
    errorCount: errors.length,
    avgDurationMs: values.length
      ? Number((values.reduce((a, b) => a + b, 0) / values.length).toFixed(0))
      : null,
    p95DurationMs: percentile(values, 95),
    ttfbP95Ms: percentile(ttfbs, 95),
  };
}

export function selectMediaSnapshot(records) {
  const mediaPipeBytes = byMetric(records, 'media', 'mediapipe_download_bytes');
  const mediaPipeDuration = byMetric(records, 'media', 'mediapipe_download_duration_ms');
  const videoWaiting = byMetric(records, 'media', 'video_waiting');
  const videoErrors = byMetric(records, 'media', 'video_error');
  const imageErrors = byMetric(records, 'media', 'image_error');

  const bytes = latestValue(mediaPipeBytes);
  const durationMs = latestValue(mediaPipeDuration);
  const throughputMbps =
    bytes && durationMs ? Number(((bytes * 8) / 1000 / durationMs).toFixed(2)) : null;

  return {
    mediaPipeBytes: bytes,
    mediaPipeDurationMs: durationMs,
    mediaPipeThroughputMbps: throughputMbps,
    videoWaitingCount: videoWaiting.length,
    videoErrorCount: videoErrors.length,
    imageErrorCount: imageErrors.length,
  };
}

export function selectWebRtcSnapshot(records) {
  const recent = withinWindow(records, 5000);
  const rtt = byMetric(recent, 'webrtc', 'rtt').map(entry => entry.value);
  const jitter = byMetric(recent, 'webrtc', 'jitter').map(entry => entry.value);
  const packetsLost = byMetric(recent, 'webrtc', 'packets_lost');
  const bitrate = byMetric(recent, 'webrtc', 'inbound_bitrate').map(entry => entry.value);
  const setupTimes = byMetric(records, 'webrtc', 'connection_setup_time');
  const reconnects = byMetric(records, 'webrtc', 'reconnect_scheduled');

  return {
    setupMs: latestValue(setupTimes),
    rtt: rtt.length ? rtt[rtt.length - 1] : null,
    jitter: jitter.length ? jitter[jitter.length - 1] : null,
    packetsLostLatest: latestValue(packetsLost),
    inboundBitrateKbps: bitrate.length ? bitrate[bitrate.length - 1] : null,
    reconnectCount: reconnects.length,
  };
}
